import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { notify } from "@/lib/notify";
import { AlertTriangle } from "lucide-react";


type Invite = {
  id: string;
  organization_id: string;
  email: string;
  invite_role: string;
  student_email: string | null;
  expires_at: string;
  accepted_at: string | null;
  created_by: string | null;
};


const CONSENT_VERSION = "2026-07-11";

export default function ParentAccept() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);
  const [consents, setConsents] = useState({
    tos: false,
    privacy: false,
    parent: false,
    notifications: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      // Invite details are resolved server-side by the secret token only.
      const { data } = await (supabase as any).rpc("get_org_invite_by_token", {
        _token: token,
      });
      setInvite(Array.isArray(data) ? (data[0] ?? null) : (data ?? null));

      setLoading(false);
    })();
  }, [token]);

  const accept = async () => {
    if (!invite || !user) return;
    if (!consents.tos || !consents.privacy || !consents.parent) {
      toast({ title: "Please accept the required consents", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // Server-side guarded acceptance: validates the token, confirms the
      // signed-in user owns the invited email, creates the membership row and
      // marks the invite accepted. No client-side privilege required.
      const { data: accepted, error: acceptError } = await (supabase as any).rpc("accept_org_invite", {
        _token: token,
      });
      if (acceptError) throw acceptError;

      const studentId: string | null = accepted?.student_user_id ?? null;

      // link
      if (studentId) {
        await (supabase as any).from("parent_child_links").insert({
          parent_user_id: user.id,
          student_user_id: studentId,
          organization_id: invite.organization_id,
          verified_at: new Date().toISOString(),
        });
      }

      // consent record
      await (supabase as any).from("parent_consents").insert({
        parent_user_id: user.id,
        organization_id: invite.organization_id,
        student_user_id: studentId,
        tos_version: CONSENT_VERSION,
        privacy_version: CONSENT_VERSION,
        parent_consent_version: CONSENT_VERSION,
        notifications_opt_in: consents.notifications,
        consented_at: new Date().toISOString(),
      });

      const student = studentId ? { id: studentId } : null;


      // audit
      await (supabase as any).from("institution_audit_log").insert([
        {
          organization_id: invite.organization_id,
          action: "parent_invite_accepted",
          parent_user_id: user.id,
          student_user_id: student?.id ?? null,
          performed_by: user.id,
        },
        {
          organization_id: invite.organization_id,
          action: "parent_consent_granted",
          parent_user_id: user.id,
          student_user_id: student?.id ?? null,
          performed_by: user.id,
          details: { version: CONSENT_VERSION, notifications: consents.notifications },
        },
      ]);

      // Notify school-side stakeholders that this parent joined + consented.
      const notifyIds: string[] = [];
      if (invite.created_by) notifyIds.push(invite.created_by);
      const { data: admins } = await (supabase as any)
        .from("organization_members")
        .select("user_id")
        .eq("organization_id", invite.organization_id)
        .eq("member_role", "institution_admin")
        .eq("status", "active");
      for (const a of (admins ?? []) as { user_id: string }[]) notifyIds.push(a.user_id);

      const childLabel = student?.id ? "their child" : "a learner";
      await notify({
        userIds: notifyIds,
        type: "parent_invite_accepted",
        title: "Parent joined WeKIT™",
        body: `A parent accepted the invitation for ${childLabel}.`,
        organizationId: invite.organization_id,
        data: { parent_user_id: user.id, student_user_id: student?.id ?? null },
      });
      await notify({
        userIds: notifyIds,
        type: "parent_consent_granted",
        title: "Parent consent granted",
        body: `Consent v${CONSENT_VERSION} recorded.`,
        organizationId: invite.organization_id,
        data: {
          parent_user_id: user.id,
          student_user_id: student?.id ?? null,
          version: CONSENT_VERSION,
        },
      });

      toast({ title: "Welcome to WeKIT™", description: "Your parent account is active." });
      navigate("/parent");

    } catch (e: any) {
      toast({ title: "Something went wrong", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="container py-10 text-muted-foreground">Checking invitation…</div>;

  if (!invite || invite.accepted_at || new Date(invite.expires_at) < new Date()) {
    const status = !invite
      ? "invalid"
      : invite.accepted_at
        ? "used"
        : "expired";
    const heading =
      status === "expired"
        ? "This invitation has expired"
        : status === "used"
          ? "This invitation was already accepted"
          : "Invitation not found";
    const nextSteps =
      status === "expired"
        ? "Invitations are valid for 14 days. Ask your child's teacher or school admin to send a new link — they can regenerate it from the Teacher Dashboard in one click."
        : status === "used"
          ? "If this wasn't you, contact the school so they can review access. Otherwise, sign in and head to your Parent Dashboard."
          : "The link may be mistyped. Please request a fresh invitation from your child's school.";
    return (
      <div className="container max-w-lg py-16">
        <Card>
          <CardHeader className="items-center text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mb-2" aria-hidden />
            <CardTitle>{heading}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-center">
            <p className="text-sm text-muted-foreground">{nextSteps}</p>
            <div className="rounded-lg bg-muted/40 p-4 text-left text-sm space-y-2">
              <p className="font-medium">What to do next</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Reply to the email you received and ask for a new link.</li>
                <li>Or contact your child's school directly — teachers can resend or regenerate the invitation instantly.</li>
                {status === "used" && (
                  <li>Already accepted? Sign in and open your Parent Dashboard.</li>
                )}
              </ol>
            </div>
            <div className="flex gap-2 justify-center">
              <Link to="/"><Button variant="outline">Home</Button></Link>
              {status === "used" ? (
                <Link to="/parent"><Button>Go to Parent Dashboard</Button></Link>
              ) : (
                <Link to="/auth"><Button>Sign in</Button></Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }


  if (!user) {
    return (
      <div className="container max-w-lg py-16 text-center">
        <h1 className="text-2xl font-bold mb-3">Sign in to accept</h1>
        <p className="text-muted-foreground mb-6">
          Please create an account with <strong>{invite.email}</strong> to link with your child.
        </p>
        <Link to={`/auth?redirect=/parent/accept?token=${token}`}>
          <Button>Sign in / Sign up</Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <PageHead title="Accept Parent Invitation | WeKIT™" description="Consent & link your child" path="/parent/accept" />
      <div className="container max-w-xl py-12">
        <Card>
          <CardHeader>
            <CardTitle>Welcome, Parent 🌱</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              You've been invited to walk alongside your child on their WeKIT™ Career Clarity 360 journey.
              Before we link your accounts, please review and accept the following.
            </p>

            <ConsentRow
              checked={consents.tos}
              onChange={(v) => setConsents({ ...consents, tos: v })}
              label={<>I accept the <Link to="/terms" className="underline">Terms of Service</Link>.</>}
            />
            <ConsentRow
              checked={consents.privacy}
              onChange={(v) => setConsents({ ...consents, privacy: v })}
              label={<>I have read the <Link to="/privacy" className="underline">Privacy Policy</Link>.</>}
            />
            <ConsentRow
              checked={consents.parent}
              onChange={(v) => setConsents({ ...consents, parent: v })}
              label="I consent to view my child's assessment results and growth journey as their parent/guardian."
            />
            <ConsentRow
              checked={consents.notifications}
              onChange={(v) => setConsents({ ...consents, notifications: v })}
              label="Send me monthly progress updates and helpful parent nudges (optional)."
            />

            <Button className="w-full" onClick={accept} disabled={submitting}>
              {submitting ? "Linking…" : "Accept & continue"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function ConsentRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} className="mt-0.5" />
      <span className="text-sm leading-snug">{label}</span>
    </label>
  );
}
